import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { translations } from '../locales/translations';
import { REGIONS } from './regions';
import {
  WORLD_LANGUAGES,
  detectLanguageFromDevice,
  detectLanguageFromRegion,
  getFallbackLang,
} from './worldLanguages';

/* ─── expo-localization (graceful fallback) ─────────────────── */
let Localization = null;
try { Localization = require('expo-localization'); } catch (_) {}

/* ─── AsyncStorage (persistance choix de langue v1.12.256) ──── */
let AsyncStorage = null;
try { AsyncStorage = require('@react-native-async-storage/async-storage').default; } catch (_) {}
const LANG_STORAGE_KEY = 'mytenue_user_lang';

/* ─── Module-level lang cache (lecture sync hors React) ─────────
 * Utilisé par les services natifs (push notifications, reminders…)
 * qui n'ont pas accès au contexte React. Initialisé au boot via
 * AsyncStorage et synchronisé à chaque setLang().
 */
let _currentLang = 'en'; // v1.12.267 — défaut EN (audience internationale)
export function getCurrentLang() { return _currentLang; }
export function _setCurrentLangCache(lang) {
  if (lang && typeof lang === 'string') _currentLang = lang;
}

// Init asynchrone au chargement du module (best-effort)
(function initLangCache() {
  try {
    if (AsyncStorage) {
      AsyncStorage.getItem(LANG_STORAGE_KEY).then(saved => {
        if (saved && typeof saved === 'string') _currentLang = saved;
      }).catch(() => {});
    }
  } catch (_) {}
})();

/* ─── Exchange rates ────────────────────────────────────────── */
export const EXCHANGE_RATES = {
  EUR:1, USD:1.08, GBP:0.86, CNY:7.84, TWD:35.2, HKD:8.42,
  JPY:161, KRW:1450, THB:38.5, IDR:17200, VND:27400,
  SGD:1.46, MYR:5.08, PHP:62.3, INR:90.5, AED:3.97,
  SAR:4.05, BRL:5.40, MXN:18.20, RUB:99, TRY:34.8,
  PLN:4.28, SEK:11.2, NOK:11.5, DKK:7.46, CHF:0.96,
  AUD:1.65, NZD:1.79, CAD:1.46, NGN:1620, ZAR:20.1, KES:138,
};

export function formatPrice(amount, currency = 'EUR') {
  const n = Math.round(amount);
  const fmt = {
    EUR:v=>`${v} €`,     USD:v=>`$${v}`,      GBP:v=>`£${v}`,
    CNY:v=>`¥${v}`,      TWD:v=>`NT$${v}`,    HKD:v=>`HK$${v}`,
    JPY:v=>`¥${v.toLocaleString('ja-JP')}`,
    KRW:v=>`₩${v.toLocaleString('ko-KR')}`,
    THB:v=>`฿${v}`,
    IDR:v=>`Rp${v.toLocaleString('id-ID')}`,
    VND:v=>`₫${v.toLocaleString('vi-VN')}`,
    INR:v=>`₹${v}`,      AED:v=>`${v} د.إ`,   SAR:v=>`${v} ر.س`,
    BRL:v=>`R$ ${v}`,    MXN:v=>`MX$${v}`,    RUB:v=>`${v} ₽`,
    TRY:v=>`${v} ₺`,     SGD:v=>`S$${v}`,     MYR:v=>`RM${v}`,
    PHP:v=>`₱${v}`,      SEK:v=>`${v} kr`,    NOK:v=>`${v} kr`,
    DKK:v=>`${v} kr`,    CHF:v=>`CHF ${v}`,   PLN:v=>`${v} zł`,
    AUD:v=>`A$${v}`,     NZD:v=>`NZ$${v}`,    CAD:v=>`C$${v}`,
    NGN:v=>`₦${v}`,      ZAR:v=>`R${v}`,      KES:v=>`KSh${v}`,
  };
  return (fmt[currency] || fmt.EUR)(n);
}

export function convertPrice(amountEUR, toCurrency) {
  return amountEUR * (EXCHANGE_RATES[toCurrency] || 1);
}

/* ─── Payment methods per region ───────────────────────────── */
export const PAYMENT_METHODS = {
  CN:['alipay','wechat','unionpay','apple','google'],
  TW:['linepay','jkopay','apple','google'],
  HK:['fps','octopus','apple','google'],
  JP:['paypay','rakuten_pay','apple','google'],
  KR:['kakaopay','naverpay','toss','apple','google'],
  TH:['promptpay','truemoney','apple','google'],
  ID:['gopay','ovo','dana','google'],
  VN:['momo_vn','zalopay','google'],
  SG:['paynow','apple','google'],
  MY:['tng','boost','apple','google'],
  PH:['gcash','maya','apple','google'],
  IN:['upi','paytm','google','apple'],
  SA:['mada','stcpay','apple','google'],
  AE:['apple','google'],
  TR:['apple','google'],
  BR:['pix','apple','google'],
  default:['apple','google','visa','mastercard'],
};

/* ─── Auto-detect from device ───────────────────────────────── */
function autoDetectLanguage() {
  // v1.12.267 — Web fallback : lit navigator.language directement si expo-localization indisponible
  // (Mexique avec navigateur EN doit voir l'app en EN, pas FR par défaut)
  try {
    if (Localization) {
      var locales = [];
      try {
        var raw = Localization.getLocales ? Localization.getLocales() : [];
        locales = Array.isArray(raw) ? raw : [];
      } catch (_) {}
      if (locales.length > 0) return detectLanguageFromDevice(locales);
    }
  } catch (_) {}
  // Fallback web : lecture directe navigator.language(s)
  try {
    if (typeof navigator !== 'undefined') {
      var nav = navigator.languages || (navigator.language ? [navigator.language] : []);
      if (nav && nav.length > 0) {
        var fakeLocales = nav.map(function(l) { return { languageTag: l, languageCode: (l || '').split('-')[0] }; });
        var detected = detectLanguageFromDevice(fakeLocales);
        if (detected && detected !== 'fr') return detected; // si non-fr → garde le détecté
        // Si fr détecté mais navigator dit autre chose (ex: en-US, es-MX), prefère ce dernier
        var first = (nav[0] || '').toLowerCase();
        if (!first.startsWith('fr')) return 'en';
        return 'fr';
      }
    }
  } catch (_) {}
  return 'en'; // v1.12.267 — défaut EN au lieu de FR (audience internationale)
}

/* ─── Context ────────────────────────────────────────────────── */
const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang,     setLang]     = useState(() => {
    const detected = autoDetectLanguage();
    _setCurrentLangCache(detected);
    return detected;
  });
  const [region,   setRegion]   = useState('FR');
  const [currency, setCurrency] = useState('EUR');

  // Synchronise le cache module à chaque changement de lang (services natifs)
  useEffect(() => { _setCurrentLangCache(lang); }, [lang]);

  // v1.12.256 — Charge le choix manuel persisté au démarrage (override l'auto-détection)
  // v1.12.347 — émet 'user_lang_manual_change' pour bloquer l'écrasement par l'event
  //   OAuth `user_preferred_locale` qui arrive après l'auth (Google/Apple) et
  //   resetait la langue à celle du compte backend à chaque reload.
  useEffect(() => {
    if (!AsyncStorage) return;
    AsyncStorage.getItem(LANG_STORAGE_KEY).then(saved => {
      if (saved && translations[saved]) {
        console.log('[i18n] loaded persisted lang:', saved);
        setLang(saved);
        try {
          const RN = require('react-native');
          RN?.DeviceEventEmitter?.emit?.('user_lang_manual_change', saved);
        } catch {}
      }
    }).catch(() => {});
  }, []);

  // v1.12.256 — setLang wrapper qui persiste + émet event
  const setLangPersisted = useCallback((newLang) => {
    if (!translations[newLang]) return;
    setLang(newLang);
    if (AsyncStorage) AsyncStorage.setItem(LANG_STORAGE_KEY, newLang).catch(() => {});
    try {
      const RN = require('react-native');
      const emitter = RN?.DeviceEventEmitter;
      if (emitter?.emit) emitter.emit('user_lang_manual_change', newLang);
    } catch {}
  }, []);

  // ═══ Écoute la préférence de langue depuis OAuth provider ═══
  // Quand l'user se connecte avec Google ou Apple, son provider fournit une
  // locale (claim `locale` de Google, ou locale système pour Apple). On
  // l'applique automatiquement la première fois (priorité au manuel ensuite).
  useEffect(() => {
    let manualOverride = false;
    let sub = null;
    let sub2 = null;
    try {
      const RN = require('react-native');
      const emitter = RN && RN.DeviceEventEmitter;
      if (!emitter || typeof emitter.addListener !== 'function') return;

      sub = emitter.addListener('user_preferred_locale', (newLang) => {
        try {
          if (manualOverride) return;
          if (!newLang || typeof newLang !== 'string') return;
          if (translations[newLang]) {
            console.log('[i18n] applying preferred locale from OAuth:', newLang);
            setLang(newLang);
          }
        } catch (_) {}
      });
      sub2 = emitter.addListener('user_lang_manual_change', () => {
        manualOverride = true;
      });
    } catch (_) {}

    return () => {
      try { if (sub && typeof sub.remove === 'function') sub.remove(); } catch (_) {}
      try { if (sub2 && typeof sub2.remove === 'function') sub2.remove(); } catch (_) {}
    };
  }, []);

  const dict = translations[lang]
    || translations[getFallbackLang(lang)]
    || translations['fr'];

  const t      = useCallback((key) => dict[key] ?? key, [dict]);
  const isRTL  = WORLD_LANGUAGES[lang]?.rtl ?? false;
  const langInfo = WORLD_LANGUAGES[lang] || WORLD_LANGUAGES['fr'];

  const setRegionAndLang = useCallback((regionCode) => {
    setRegion(regionCode);
    const detected = detectLanguageFromRegion(regionCode);
    setLang(detected);
    if (REGIONS[regionCode]) {
      setCurrency(REGIONS[regionCode].currency);
    }
  }, []);

  const price = useCallback(
    (amountEUR) => formatPrice(convertPrice(amountEUR, currency), currency),
    [currency]
  );

  return (
    <I18nContext.Provider value={{
      t, lang, setLang: setLangPersisted, langInfo, isRTL,
      region, setRegionAndLang,
      currency, setCurrency, price,
      allLanguages: WORLD_LANGUAGES,
    }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}

export { WORLD_LANGUAGES };
